/**
 * 我的钱包 · 卖家提现（N18 / PRD §5.6.3 卖家出金）
 *
 * 背景：M6「提现/银行通道」此前全库无实现，卖家完成交易后托管资金无法出金。
 * 本页打通「查看余额 → 申请提现（支付密码 2FA）→ 跟踪打款状态」的卖家侧闭环。
 *
 * 后端契约（handler/escrow_withdraw.go）：金额一律「分」，前端展示除以 100。
 *
 * ⚠️ 诚实边界：当前无真实银行通道，提现单提交后为「待打款」，需平台运营线下打款后
 * 手工确认；页面文案不得承诺自动到账。
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getPaymentPasswordStatus } from '@/api/payment';
import {
  WITHDRAW_MIN_CENTS,
  WITHDRAW_STATUS,
  WITHDRAW_STATUS_LABEL,
  centsToYuan,
  createWithdrawal,
  getEscrowAccount,
  listMyWithdrawals,
  maskBankAccount,
  type EscrowBalance,
  type EscrowWithdrawal,
} from '@/api/escrow';
import { validatePayPassword } from '@/utils/paymentPassword';
import { formatDateTime, formatMoney } from '@/utils/format';
import Empty from '@/components/common/Empty';
import PageHero from '@/components/common/PageHero';
import '@/styles/pages/wallet.css';

/** 提现单状态 → 展示色调 */
const STATUS_TONE: Record<number, string> = {
  [WITHDRAW_STATUS.PENDING]: 'warning',
  [WITHDRAW_STATUS.PAID]: 'success',
  [WITHDRAW_STATUS.REJECTED]: 'danger',
};

interface WithdrawFormState {
  amountYuan: string;
  bankName: string;
  bankAccount: string;
  accountName: string;
  paymentPassword: string;
}

const EMPTY_FORM: WithdrawFormState = {
  amountYuan: '',
  bankName: '',
  bankAccount: '',
  accountName: '',
  paymentPassword: '',
};

export default function Wallet() {
  const [balance, setBalance] = useState<EscrowBalance | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [balanceError, setBalanceError] = useState('');

  const [list, setList] = useState<EscrowWithdrawal[]>([]);
  const [listLoading, setListLoading] = useState(true);

  const [pwdSet, setPwdSet] = useState<boolean | null>(null); // null = 未知/加载失败
  const [form, setForm] = useState<WithdrawFormState>(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [formMsg, setFormMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadBalance = useCallback(async () => {
    setBalanceLoading(true);
    setBalanceError('');
    try {
      setBalance(await getEscrowAccount());
    } catch (e) {
      setBalanceError(e instanceof Error ? e.message : '余额加载失败');
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      setList(await listMyWithdrawals());
    } catch {
      // 提现单加载失败不阻断余额与申请入口，静默为空
      setList([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  const loadPwdStatus = useCallback(async () => {
    try {
      const s = await getPaymentPasswordStatus();
      setPwdSet(s.set);
    } catch {
      setPwdSet(null);
    }
  }, []);

  useEffect(() => {
    loadBalance();
    loadList();
    loadPwdStatus();
  }, [loadBalance, loadList, loadPwdStatus]);

  const patch = (key: keyof WithdrawFormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  /** 「全部提现」：按可提现余额回填（元，去掉多余小数） */
  const fillAll = () => {
    const yuan = centsToYuan(balance?.withdrawable ?? 0);
    if (yuan <= 0) {
      setFormError('当前可提现余额为 0，暂无可提现金额');
      return;
    }
    setFormError('');
    patch('amountYuan', yuan.toFixed(2));
  };

  /**
   * 提交提现：前端先行校验（金额/账户信息/支付密码格式），
   * 支付密码错误码（2006/2007）由后端返回，此处透出可读文案。
   */
  const handleSubmit = async () => {
    setFormError('');
    setFormMsg('');

    const yuan = Number(form.amountYuan);
    if (!form.amountYuan || Number.isNaN(yuan) || yuan <= 0) {
      setFormError('请输入正确的提现金额');
      return;
    }
    const amountCents = Math.round(yuan * 100); // 元 → 分，四舍五入消除浮点误差
    if (amountCents < WITHDRAW_MIN_CENTS) {
      setFormError(`提现金额不得低于 ${centsToYuan(WITHDRAW_MIN_CENTS)} 元`);
      return;
    }
    if (balance && amountCents > balance.withdrawable) {
      setFormError(`提现金额超出可提现余额（${formatMoney(centsToYuan(balance.withdrawable))}）`);
      return;
    }
    if (!form.bankName.trim()) {
      setFormError('请填写开户行');
      return;
    }
    if (!/^\d{8,32}$/.test(form.bankAccount.replace(/\s/g, ''))) {
      setFormError('银行账号需为 8-32 位数字');
      return;
    }
    if (!form.accountName.trim()) {
      setFormError('请填写账户名');
      return;
    }
    const pwdMsg = validatePayPassword(form.paymentPassword);
    if (pwdMsg) {
      setFormError(pwdMsg);
      return;
    }

    setSubmitting(true);
    try {
      const wd = await createWithdrawal({
        amount_cents: amountCents,
        payment_password: form.paymentPassword,
        bank_name: form.bankName.trim(),
        bank_account: form.bankAccount.replace(/\s/g, ''),
        account_name: form.accountName.trim(),
      });
      setFormMsg(`提现申请已提交（单号 ${wd.withdraw_no}），平台确认打款后到账`);
      setForm(EMPTY_FORM);
      await Promise.all([loadBalance(), loadList()]);
    } catch (e) {
      const err = e as { message?: string; code?: number };
      // 2004 未设置支付密码 → 提示去设置
      if (err.code === 2004) {
        setPwdSet(false);
      }
      setFormError(err.message ?? '提现申请失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const withdrawable = centsToYuan(balance?.withdrawable ?? 0);

  return (
    <div className="wallet-page">
      <PageHero
        eyebrow="MY WALLET"
        title="我的钱包"
        desc="托管资金余额 · 提现申请 · 打款进度"
      />
      <div className="container">
        {/* 余额概览 */}
        <div className="wallet-cards">
          <div className="wallet-card wallet-card--primary">
            <span>可提现余额</span>
            {balanceLoading ? (
              <b>--</b>
            ) : (
              <b>{formatMoney(withdrawable)}</b>
            )}
            <em>可用余额扣除冻结后可用于提现的部分</em>
          </div>
          <div className="wallet-card">
            <span>待打款</span>
            <b>{formatMoney(centsToYuan(balance?.pending_cents ?? 0))}</b>
            <em>已提交申请、等待平台打款的金额</em>
          </div>
          <div className="wallet-card">
            <span>冻结金额</span>
            <b>{formatMoney(centsToYuan(balance?.frozen ?? 0))}</b>
            <em>交易进行中暂被冻结的托管资金</em>
          </div>
        </div>

        {balanceError ? (
          <div className="form-error" style={{ marginBottom: 16 }}>
            {balanceError}
            <button className="btn btn-sm" style={{ marginLeft: 8 }} onClick={loadBalance}>
              重试
            </button>
          </div>
        ) : null}

        <div className="wallet-layout">
          {/* 提现申请 */}
          <div className="wallet-panel">
            <div className="wallet-panel__head">
              申请提现
              <span className="wallet-hint">提现需验证支付密码（资金安全 2FA）</span>
            </div>

            {pwdSet === false ? (
              <div className="wallet-notice">
                您尚未设置支付密码，提现无法提交。
                <Link to="/my?tab=security">前往设置支付密码 →</Link>
              </div>
            ) : null}

            <div className="wallet-form">
              <div className="form-field">
                <label className="form-label" htmlFor="wd-amount">
                  <span className="req">*</span>提现金额（元）
                </label>
                <div className="wallet-amount">
                  <input
                    id="wd-amount"
                    className="form-control"
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder={`最低 ${centsToYuan(WITHDRAW_MIN_CENTS)} 元`}
                    value={form.amountYuan}
                    onChange={(e) => patch('amountYuan', e.target.value)}
                  />
                  <button type="button" className="btn btn-outline btn-sm" onClick={fillAll}>
                    全部提现
                  </button>
                </div>
                <div className="wallet-hint">当前可提现 {formatMoney(withdrawable)}</div>
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor="wd-bank">
                  <span className="req">*</span>开户行
                </label>
                <input
                  id="wd-bank"
                  className="form-control"
                  type="text"
                  maxLength={64}
                  placeholder="如：中国农业银行济南高新支行"
                  value={form.bankName}
                  onChange={(e) => patch('bankName', e.target.value)}
                />
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor="wd-account">
                  <span className="req">*</span>银行账号
                </label>
                <input
                  id="wd-account"
                  className="form-control"
                  type="text"
                  maxLength={32}
                  placeholder="8-32 位数字"
                  value={form.bankAccount}
                  onChange={(e) => patch('bankAccount', e.target.value)}
                />
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor="wd-name">
                  <span className="req">*</span>账户名
                </label>
                <input
                  id="wd-name"
                  className="form-control"
                  type="text"
                  maxLength={64}
                  placeholder="需与银行预留姓名/企业名一致"
                  value={form.accountName}
                  onChange={(e) => patch('accountName', e.target.value)}
                />
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor="wd-pwd">
                  <span className="req">*</span>支付密码
                  <span className="hint">连续输错 5 次将锁定 15 分钟</span>
                </label>
                <input
                  id="wd-pwd"
                  className="form-control"
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="6 位数字"
                  value={form.paymentPassword}
                  onChange={(e) => patch('paymentPassword', e.target.value.replace(/\D/g, ''))}
                />
              </div>

              {formError ? <div className="form-error">{formError}</div> : null}
              {formMsg ? <div className="form-success">{formMsg}</div> : null}

              <button
                className="btn btn-primary btn-block"
                onClick={handleSubmit}
                disabled={submitting || pwdSet === false}
              >
                {submitting ? '提交中…' : '提交提现申请'}
              </button>

              <p className="wallet-note">
                提现申请提交后资金即时从可用余额划出；平台确认线下打款后状态更新为「已打款」，
                若审核未通过则原路退回余额。
              </p>
            </div>
          </div>

          {/* 提现记录 */}
          <div className="wallet-panel">
            <div className="wallet-panel__head">提现记录</div>
            {listLoading ? (
              <Empty icon="⏳" title="加载中…" />
            ) : list.length === 0 ? (
              <Empty icon="💸" title="暂无提现记录" desc="完成交易收款后即可申请提现" />
            ) : (
              <div className="wallet-records">
                {list.map((w) => (
                  <div className="wallet-record" key={w.id}>
                    <div className="wallet-record__main">
                      <div className="wallet-record__row">
                        <strong>{formatMoney(centsToYuan(w.amount_cents))}</strong>
                        <span className={`wallet-tag is-${STATUS_TONE[w.status] ?? 'warning'}`}>
                          {WITHDRAW_STATUS_LABEL[w.status] ?? w.status}
                        </span>
                      </div>
                      <div className="wallet-record__meta">
                        <span>单号 {w.withdraw_no}</span>
                        <span>·</span>
                        <span>
                          {w.bank_name} {maskBankAccount(w.bank_account)}
                        </span>
                      </div>
                      <div className="wallet-record__meta">
                        <span>申请 {formatDateTime(w.created_at)}</span>
                        {w.reviewed_at ? (
                          <>
                            <span>·</span>
                            <span>处理 {formatDateTime(w.reviewed_at)}</span>
                          </>
                        ) : null}
                      </div>
                      {w.status === WITHDRAW_STATUS.REJECTED && w.reject_reason ? (
                        <div className="wallet-record__reject">驳回原因：{w.reject_reason}</div>
                      ) : null}
                    </div>
                    <div className="wallet-record__side">
                      <span>预计到账</span>
                      <b>{formatMoney(centsToYuan(w.actual_cents))}</b>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
